import type { FC } from 'react';
import { Link, Text } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import type { SystemStyleObject } from '@chakra-ui/react';
import { useCustomerGeoLocationStore } from '../../../bff-api/stores/useCustomerGeoLocationStore';
import { PageUrl } from '../../../pages/utils/page-url';
import { useCustomPreferenceOrDefault, useUrl } from '../../../utils/api';
import { CustomPreference } from '../../../utils/api/CustomPreference';
import { toGoogleMapsURL } from '../../../utils/url-utils';

const textStyle: SystemStyleObject = {
    fontFamily: 'Lato,sans-serif',
    fontSize: '0.75rem',
    fontWeight: '400',
    lineHeight: 'normal',
    letterSpacing: '0.05313rem',
    color: 'slt-black.900',
};

export const FindUs: FC = () => {
    const { isLoading, data } = useCustomerGeoLocationStore();
    const enableRioSEO = useCustomPreferenceOrDefault(CustomPreference.ENABLE_RIO_SEO, true);
    const storesFindUrl = useUrl(PageUrl.STORES_FIND);
    const classUrl = useUrl(PageUrl.SEARCH_SHOW, ['cgid', 'in-store-classes']);
    const storesUrl = enableRioSEO ? PageUrl.STORES : storesFindUrl;
    const googleMapsUrl = toGoogleMapsURL(data);

    return !isLoading && data?.name ? (
        <div className="flex flex-col gap-5 sm:gap-4" data-testid="footer-find-us-container">
            <div className="hidden sm:max-md:block">
                <Text
                    data-testid="footer.find_us.title"
                    fontFamily="Lato,sans-serif"
                    fontSize="1rem"
                    fontWeight="700"
                    lineHeight="1.1875rem"
                    letterSpacing="-0.01563rem"
                    textTransform="uppercase"
                >
                    <FormattedMessage id="footer.find_us.title" defaultMessage="Find Us" />
                </Text>
            </div>

            <div className="mt-2 sm:mt-0">
                <Link
                    data-testid="footer.find_us.find_a_store.link"
                    href={storesUrl}
                    sx={{
                        ...textStyle,
                        textDecorationLine: 'underline',
                    }}
                >
                    <FormattedMessage
                        id="footer.find_us.find_a_store"
                        defaultMessage="Find a store"
                    />
                </Link>
            </div>

            {data.c_participateInCulinary ? (
                <Link
                    data-testid="footer.find_us.view_class.link"
                    href={classUrl}
                    sx={{
                        ...textStyle,
                        fontSize: '0.875rem',
                        fontWeight: 900,
                        letterSpacing: '0.0625rem',
                        textDecorationLine: 'underline',
                        textTransform: 'uppercase',
                    }}
                >
                    <FormattedMessage
                        id="footer.find_us.menu.view_class"
                        defaultMessage="View Class Schedule"
                    />
                </Link>
            ) : null}

            <div className="flex gap-2 sm:max-md:flex-col sm:max-md:gap-4">
                <Link
                    data-testid="footer.find_us.get_directions.link"
                    href={googleMapsUrl}
                    sx={{
                        ...textStyle,
                        textDecorationLine: 'underline',
                    }}
                    isExternal
                >
                    <FormattedMessage
                        id="footer.find_us.get_directions"
                        defaultMessage="Get Directions"
                    />
                </Link>

                <Text
                    sx={{
                        ...textStyle,
                        display: {
                            base: 'none',
                            md: 'block',
                        },
                    }}
                >
                    |
                </Text>

                <Link
                    data-testid="footer.find_us.view_details.link"
                    href={data.url}
                    sx={{
                        ...textStyle,
                        textDecorationLine: 'underline',
                    }}
                >
                    <FormattedMessage
                        id="footer.find_us.view_details"
                        defaultMessage="View Details"
                    />
                </Link>
            </div>
        </div>
    ) : null;
};
